import { useMemo, useState } from 'react';
import { fmtNum, fmtTRY } from '../../yeni-design/primitives';
import { useSaSorgu } from '../useSatisVerisi';
import { sayi, type SaKategoriSatiri, type Sayisal } from '../ozetHesaplari';
import { oranMetni, puanMetni } from '../bicim';
import { urunMarji } from '../CokSatanBlok';
import type { DetayGovdeProps } from './detayTipleri';
import { AramaKutusu, aramaSuz, bos, DeltaHucre, DetayBolum, DetayMesaj, DetayYukleniyor, OzetKutulari } from './ortak';

/** 990056 Satış Analiz · Ürün Kârlılığı (SQL 137:203-208). */
export interface UrunKarSatiri {
  stok_kodu: string;
  urun_adi: string;
  kategori: string;
  ciro: Sayisal;
  maliyet: Sayisal;
  kar: Sayisal;
  kar_marji: Sayisal;
}

export interface KategoriUrunu {
  stokKodu: string;
  urunAdi: string;
  ciro: number;
  pay: number | null;
  marj: number | null;
  oncekiCiro: number | null;
}

/**
 * Kategori urunleri, ciroya gore. Marj ekranin TEK tanimi (kar ÷ urun cirosu, `urunMarji`). Onceki ciro URUNUN
 * KENDI onceki cirosudur (kategorisi degismis olsa da) — Δ "bu urun gecen donem ne yapti" sorusudur.
 */
export function kategoriUrunleri(bu: UrunKarSatiri[], onceki: UrunKarSatiri[], kategori: string): KategoriUrunu[] {
  const kat = bu.filter((r) => String(r.kategori) === kategori);
  const toplam = kat.reduce((t, r) => t + sayi(r.ciro), 0);
  const oncekiMap = new Map<string, number>();
  for (const r of onceki) {
    const kod = String(r.stok_kodu);
    oncekiMap.set(kod, (oncekiMap.get(kod) ?? 0) + sayi(r.ciro));
  }
  return kat
    .map((r) => {
      const ciro = sayi(r.ciro);
      const kod = String(r.stok_kodu);
      return {
        stokKodu: kod,
        urunAdi: String(r.urun_adi),
        ciro,
        pay: toplam !== 0 ? (ciro / toplam) * 100 : null,
        marj: urunMarji(r),
        oncekiCiro: oncekiMap.get(kod) ?? null,
      };
    })
    .sort((a, b) => b.ciro - a.ciro);
}

/** Ozet, kategori blogunun kaynagi 990051'den (SOZLESME SAPMASI 2): ayni sayi, ayni tanim. */
export function kategoriOzeti(k: SaKategoriSatiri) {
  const ciro = sayi(k.ciro);
  const marj = ciro !== 0 ? (sayi(k.kar) / ciro) * 100 : null;
  const oncekiCiro = sayi(k.onceki_ciro);
  const oncekiMarj = !bos(k.onceki_ciro) && oncekiCiro !== 0 ? (sayi(k.onceki_kar) / oncekiCiro) * 100 : null;
  return {
    ciro,
    pay: bos(k.pay) ? null : sayi(k.pay),
    marj,
    marjFarki: marj !== null && oncekiMarj !== null ? marj - oncekiMarj : null,
    aUrun: sayi(k.a_urun),
  };
}

/** M1 — kategori detayi (spec §7.5). Istek: 990056 bu + onceki donem (2); 990051 sayfa onbelleginden. */
export function KategoriDetay({ kaynaklar, donem, oncekiDonem, sube, kanal, ac, kategori }: DetayGovdeProps & { kategori: string }) {
  const [arama, setArama] = useState('');
  const katQ = useSaSorgu<SaKategoriSatiri>(
    kaynaklar.kategori, { donem, sube, kanal, onceki: oncekiDonem }, true, { onbellekten: true },
  );
  const buQ = useSaSorgu<UrunKarSatiri>(kaynaklar.urunKarliligi, { donem, sube, kanal });
  const oncekiQ = useSaSorgu<UrunKarSatiri>(kaynaklar.urunKarliligi, { donem: oncekiDonem, sube, kanal });
  const buVeri = buQ.data?.data;
  const oncekiVeri = oncekiQ.data?.data;
  const urunler = useMemo(
    () => kategoriUrunleri(buVeri ?? [], oncekiVeri ?? [], kategori),
    [buVeri, oncekiVeri, kategori],
  );
  const gorunen = useMemo(() => aramaSuz(urunler, arama, (u) => [u.urunAdi, u.stokKodu]), [urunler, arama]);

  const katSatiri = (katQ.data?.data ?? []).find((r) => r.tur === 'kategori' && String(r.ad) === kategori);
  const oz = katSatiri ? kategoriOzeti(katSatiri) : null;

  let urunBolumu;
  if (!kaynaklar.urunKarliligi) {
    urunBolumu = <DetayMesaj testid="kategori-detay-kaynak-yok">Ürün kırılımı bu firmada tanımlı değil.</DetayMesaj>;
  } else if (buQ.isLoading || oncekiQ.isLoading) {
    urunBolumu = <DetayYukleniyor testid="kategori-detay-yukleniyor" />;
  } else if (buQ.isError) {
    urunBolumu = <DetayMesaj testid="kategori-detay-hata">Ürünler yüklenemedi.</DetayMesaj>;
  } else if (urunler.length === 0) {
    urunBolumu = <DetayMesaj testid="kategori-detay-bos">Bu kategoride dönemde ürün satışı yok.</DetayMesaj>;
  } else {
    urunBolumu = (
      <>
        <div className="flex flex-wrap items-center gap-2">
          <AramaKutusu testid="kategori-detay-ara" placeholder="Ürün ara" value={arama} onChange={setArama} />
          <span data-testid="kategori-detay-sayac" className="text-[12px] text-ink-500 tnum">
            {gorunen.length} / {urunler.length} ürün
          </span>
          {oncekiQ.isError && (
            <span data-testid="kategori-detay-onceki-hata" className="text-[12px] text-ink-500">
              · önceki dönem yüklenemedi, Δ gösterilmiyor
            </span>
          )}
        </div>
        {gorunen.length === 0 ? (
          <DetayMesaj testid="kategori-detay-arama-bos">Aramaya uyan ürün yok.</DetayMesaj>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="kategori-detay-tablo" className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="text-[12px] text-ink-500 border-b border-border">
                  <th className="text-left font-medium py-2 pr-3">Ürün</th>
                  <th className="text-right font-medium py-2 px-3">Ciro</th>
                  <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Kategorideki pay</th>
                  <th className="text-right font-medium py-2 px-3">Marj</th>
                  <th className="text-right font-medium py-2 pl-3">Δ ciro</th>
                </tr>
              </thead>
              <tbody>
                {gorunen.map((u) => (
                  <tr key={u.stokKodu} data-testid={`kategori-detay-satir-${u.stokKodu}`} className="border-b border-border last:border-b-0">
                    <td className="py-2 pr-3 max-w-0 w-full">
                      <button
                        type="button"
                        data-testid={`kategori-detay-urun-${u.stokKodu}`}
                        onClick={() => ac({ tur: 'urun', stokKodu: u.stokKodu, urunAdi: u.urunAdi })}
                        className="focus-ring block w-full truncate text-left text-ink-900 hover:underline underline-offset-2"
                      >
                        {u.urunAdi}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-right tnum whitespace-nowrap text-ink-900">{fmtTRY(u.ciro)}</td>
                    <td className="py-2 px-3 text-right tnum text-ink-500">{u.pay === null ? '—' : oranMetni(u.pay, 1)}</td>
                    <td className={`py-2 px-3 text-right tnum ${u.marj !== null && u.marj < 0 ? 'text-neg-700' : 'text-ink-500'}`}>
                      {u.marj === null ? '—' : oranMetni(u.marj, 1)}
                    </td>
                    <td className="py-2 pl-3 text-right whitespace-nowrap">
                      {oncekiQ.isError ? '—' : <DeltaHucre bu={u.ciro} onceki={u.oncekiCiro} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  return (
    <div data-testid="kategori-detay" className="flex flex-col gap-4">
      {oz && (
        <OzetKutulari
          kutular={[
            { testid: 'kategori-detay-ozet-ciro', etiket: 'Ciro', deger: fmtTRY(oz.ciro) },
            { testid: 'kategori-detay-ozet-pay', etiket: 'Pay', deger: oz.pay === null ? '—' : oranMetni(oz.pay, 1) },
            { testid: 'kategori-detay-ozet-marj', etiket: 'Marj', deger: oz.marj === null ? '—' : oranMetni(oz.marj) },
            { testid: 'kategori-detay-ozet-marj-farki', etiket: 'Marj değişimi', deger: oz.marjFarki === null ? '—' : puanMetni(oz.marjFarki) },
            { testid: 'kategori-detay-ozet-a-urun', etiket: 'A ürün', deger: fmtNum(oz.aUrun) },
          ]}
        />
      )}
      <DetayBolum baslik="Ürünler">{urunBolumu}</DetayBolum>
    </div>
  );
}
